import { controlBandChipFieldSx } from "../../theme/compact.js";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { useMemo, useState, type ReactElement } from "react";

/**
 * `<AsyncTypeaheadChipFilter>` — multi-select chip filter with
 * **hidden options until the user types**, where each keystroke
 * drives a server-side search owned by the parent.
 *
 * Spec: `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 5 /
 * AsyncTypeaheadChipFilter".
 *
 * Use when the option universe is large enough that shipping every
 * row to the client on page load is wasteful (≥ a few hundred). The
 * primitive owns the input-text state but delegates fetching to the
 * parent: pass the current page's search hits via `options` and a
 * `loading` flag, and react to `inputValue` changes by issuing a
 * debounced query keyed on the value.
 *
 * `selectedHints` is a `Map<id, label>` the parent maintains so chips
 * for items NOT currently in `options` (e.g. items selected before
 * the input was cleared, or items resolved on page-load from URL
 * state) still render with a human label rather than a UUID.
 *
 * UX matches `<TypeaheadChipFilter>`:
 * - Dropdown shows no options while the input is empty.
 * - Already-selected chips remain visible regardless of input.
 * - 280 px wide; floating label always shrunk.
 */
export interface AsyncTypeaheadChipFilterProps<T> {
  label: string;
  value: readonly string[];
  onChange: (next: readonly string[]) => void;
  /** Current search hits — the parent fetches these in response to
   *  `inputValue` changes (typically via a React Query hook with a
   *  debounce). */
  options: readonly T[];
  loading: boolean;
  /** Label cache for ids that the parent has resolved out-of-band.
   *  Lets the chip render "Sternwind" even when the search input is
   *  empty and `options` doesn't contain the selected row. */
  selectedHints: ReadonlyMap<string, string>;
  getOptionId: (option: T) => string;
  getOptionLabel: (option: T) => string;
  /** Fires whenever the user types (NOT when the primitive resets
   *  the input internally on pick/close). Parent uses this to drive
   *  a debounced search hook keyed on the value. */
  onInputChange: (query: string) => void;
  /** Per-page width override. Defaults to 280 px. */
  width?: number;
  /** Hint shown while the input is empty. Defaults to "Type to search". */
  noOptionsText?: string;
}

interface InternalOption<T> {
  id: string;
  label: string;
  raw: T | null;
}

export function AsyncTypeaheadChipFilter<T>({
  label,
  value,
  onChange,
  options,
  loading,
  selectedHints,
  getOptionId,
  getOptionLabel,
  onInputChange,
  width = 280,
  noOptionsText = "Type to search",
}: AsyncTypeaheadChipFilterProps<T>): ReactElement {
  const [input, setInput] = useState("");

  // Build a fast id → label lookup from this render's `options` so
  // any fresh server hit overrides a stale `selectedHints` entry.
  const optionsLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of options) m.set(getOptionId(o), getOptionLabel(o));
    return m;
  }, [options, getOptionId, getOptionLabel]);

  const selectedOptions = useMemo<InternalOption<T>[]>(
    () =>
      value.map((id) => ({
        id,
        label: optionsLabelById.get(id) ?? selectedHints.get(id) ?? id,
        raw: null,
      })),
    [value, optionsLabelById, selectedHints],
  );

  const searchOptions = useMemo<InternalOption<T>[]>(
    () =>
      options.map((o) => ({
        id: getOptionId(o),
        label: getOptionLabel(o),
        raw: o,
      })),
    [options, getOptionId, getOptionLabel],
  );

  const optionUnion = useMemo<InternalOption<T>[]>(() => {
    const seen = new Set<string>();
    const out: InternalOption<T>[] = [];
    for (const o of selectedOptions) {
      if (!seen.has(o.id)) {
        seen.add(o.id);
        out.push(o);
      }
    }
    for (const o of searchOptions) {
      if (!seen.has(o.id)) {
        seen.add(o.id);
        out.push(o);
      }
    }
    return out;
  }, [selectedOptions, searchOptions]);

  return (
    <Autocomplete<InternalOption<T>, true>
      multiple
      disableCloseOnSelect
      size="small"
      filterOptions={(x) => x}
      options={optionUnion}
      value={selectedOptions}
      inputValue={input}
      onInputChange={(_e, next, reason) => {
        if (reason === "reset") return;
        setInput(next);
        onInputChange(next);
      }}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.label}
      onChange={(_e, next) => {
        // Clear-on-pick — see spec §5/Clear-on-close-or-pick.
        setInput("");
        onInputChange("");
        onChange(next.map((o) => o.id));
      }}
      onClose={() => {
        // Clear-on-close — see spec §5/Clear-on-close-or-pick.
        setInput("");
        onInputChange("");
      }}
      loading={loading && input.trim().length > 0}
      sx={{
        // The control band. MISSED in #437, which is what left the
        // Turbines toolbar mixing 40px typeaheads with 32px filters
        // (Christian, 2026-09-23).
        ...controlBandChipFieldSx,
        width,
        flexShrink: 0,
        // SB6-II review fix — cap each chip's width so it never fills the whole
        // field; this reserves room for the inner <input> on the SAME row, so a
        // single picked chip no longer pushes the (empty) input onto a phantom
        // second line. Chips still wrap to real rows as more are picked.
        "& .MuiAutocomplete-tag": { maxWidth: "calc(100% - 44px)" },
      }}
      renderTags={(picked, getTagProps) =>
        picked.map((o, index) => {
          const { key, ...rest } = getTagProps({ index });
          return <Chip {...rest} key={key} size="small" label={o.label} />;
        })
      }
      renderInput={(p) => {
        const { InputLabelProps, InputProps, inputProps, ...rest } = p;
        // SB6-CONS-01 — see MultiSelectChipFilter for the rationale.
        // `minWidth: 0` lets the inner <input> sit next to the chips
        // when they fit on one row, removing the empty-row-below-chips
        // visual on Contracts / Contract-Positions / Users / Audit-Log.
        const tfProps = {
          ...rest,
          label,
          slotProps: {
            input: InputProps,
            inputLabel: { ...InputLabelProps, shrink: true },
            htmlInput: {
              ...inputProps,
              "aria-label": label,
              style: { minWidth: "0px" },
            },
          },
        } as unknown as React.ComponentProps<typeof TextField>;
        return <TextField {...tfProps} />;
      }}
      noOptionsText={
        input.trim().length === 0
          ? noOptionsText
          : loading
            ? "Searching…"
            : "No matches"
      }
    />
  );
}
